/**
 * ToggleOffSection.js — Collapsible section for toggle-off behavior
 *
 * Only shown if the action has a set_session_state effect (toggle-style).
 * Contains effects editor + variant dropdown for the deactivation path.
 */

import { useState } from 'react';
import { ChevronRight, ChevronDown } from 'lucide-react';
import { cn } from '../../../../lib/utils';
import { VALID_VARIANTS } from '../../../../engine/schema';
import ChipRow from '../chips/ChipRow';
import TypePickerGrid from '../pickers/TypePickerGrid';
import { Plus } from 'lucide-react';

const inputClass = cn(
  'bg-gray-800 border border-gray-600 rounded-md px-3 py-2 text-white text-sm',
  'focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none w-full'
);

const labelClass = 'text-sm font-medium text-gray-300 mb-1 block';

const COLOR_SCHEME = {
  chipBg: 'bg-orange-900/20',
  chipText: 'text-orange-300',
};

const ToggleOffSection = ({ action, actionIndex, dispatch }) => {
  const [expanded, setExpanded] = useState(false);
  const [showPicker, setShowPicker] = useState(false);

  // Only show if action has a set_session_state effect
  const hasToggleEffect = (action.effects || []).some(
    (e) => e.type === 'set_session_state'
  );

  if (!hasToggleEffect) return null;

  const toggleOff = action.toggleOff || {};
  const toggleEffects = toggleOff.effects || [];

  const handleUpdateEffect = (effectIndex, updated) => {
    const newEffects = [...toggleEffects];
    newEffects[effectIndex] = updated;
    dispatch({
      type: 'SET_TOGGLE_OFF',
      actionIndex,
      toggleOff: { ...toggleOff, effects: newEffects },
    });
  };

  const handleRemoveEffect = (effectIndex) => {
    const newEffects = toggleEffects.filter((_, i) => i !== effectIndex);
    dispatch({
      type: 'SET_TOGGLE_OFF',
      actionIndex,
      toggleOff: { ...toggleOff, effects: newEffects },
    });
  };

  const handleAddEffect = (newEffect) => {
    dispatch({
      type: 'SET_TOGGLE_OFF',
      actionIndex,
      toggleOff: { ...toggleOff, effects: [...toggleEffects, newEffect] },
    });
    setShowPicker(false);
  };

  const handleVariantChange = (variant) => {
    dispatch({
      type: 'SET_TOGGLE_OFF',
      actionIndex,
      toggleOff: { ...toggleOff, variant },
    });
  };

  return (
    <div className="mt-3 border border-gray-700 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className={cn(
          'w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-300',
          'hover:bg-gray-800/50 transition-colors'
        )}
      >
        {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <span className="font-medium">Toggle Off</span>
        {toggleEffects.length > 0 && (
          <span className="text-xs text-gray-500">
            ({toggleEffects.length} effect{toggleEffects.length !== 1 ? 's' : ''})
          </span>
        )}
      </button>

      {expanded && (
        <div className="px-3 pb-3 space-y-3">
          <div>
            <label className={labelClass}>Deactivation Variant</label>
            <select
              value={toggleOff.variant || 'secondary'}
              onChange={(e) => handleVariantChange(e.target.value)}
              className={inputClass}
            >
              {VALID_VARIANTS.map((v) => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
          </div>

          <div>
            <div className="text-sm font-medium text-gray-300 mb-2">
              Deactivation Effects
            </div>
            <ChipRow
              items={toggleEffects}
              chipType="effect"
              colorScheme={COLOR_SCHEME}
              onUpdate={handleUpdateEffect}
              onRemove={handleRemoveEffect}
            />

            {showPicker ? (
              <TypePickerGrid
                chipType="effect"
                onSelect={handleAddEffect}
                onCancel={() => setShowPicker(false)}
              />
            ) : (
              <button
                type="button"
                onClick={() => setShowPicker(true)}
                className={cn(
                  'mt-2 inline-flex items-center gap-1 px-3 py-1 text-sm rounded-md',
                  'border border-dashed border-orange-700 text-orange-400',
                  'hover:bg-orange-900/20 transition-colors'
                )}
              >
                <Plus size={14} />
                Add Deactivation Effect
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ToggleOffSection;
